import React, { useEffect, useRef } from 'react';
import { useSavings } from '../eximpe-landing-pacb/contexts/SavingsContext';
import { formatCurrency } from '../../utlis/currency';
import IndiaFlag from '../eximpe-main/assets/India_Icon.svg'
import { ChevronDown, Wallet, Zap } from 'lucide-react';
import { CalculatorUsedEvent, trackCalculatorEvent } from '../../utlis/useAnalytics'; 

const CurrencyCalculator: React.FC = () => {
  const calculatorRef = useRef<HTMLDivElement>(null);
  const { updateSavedAmount } = useSavings();

  useEffect(() => {
    if (!calculatorRef.current) return;

    //--------------------------------------------------
    // Element references
    //--------------------------------------------------
    const container = calculatorRef.current;
    const amountInput = container.querySelector('#amount') as HTMLInputElement;
    const sliderInput = container.querySelector('#slider') as HTMLInputElement;
    const creditValue = container.querySelector('#credit-value') as HTMLElement;
    const fxRateInfo = container.querySelector('.fx-rate-info') as HTMLElement;

    const bankCreditAmt = container.querySelector('#bank-credit-amt') as HTMLElement;
    const bankCreditPerc = container.querySelector('#bank-credit-perc') as HTMLElement;
    const paypalCreditAmt = container.querySelector('#paypal-credit-amt') as HTMLElement;
    const paypalCreditPerc = container.querySelector('#paypal-credit-perc') as HTMLElement;
    const payoneerCreditAmt = container.querySelector('#payoneer-credit-amt') as HTMLElement;
    const payoneerCreditPerc = container.querySelector('#payoneer-credit-perc') as HTMLElement;
    const savedAmountEl = container.querySelector('#saved-amount') as HTMLElement;

    const selectedCurrencyElement = container.querySelector('#selected-currency') as HTMLElement;
    const selectedCurrencyCodeElement = container.querySelector('#selected-currency-code') as HTMLElement;
    const selectedCurrencyFlagElement = container.querySelector('#selected-currency-flag') as HTMLImageElement;
    const currencyOptionsList = container.querySelector('#currency-options') as HTMLElement;
    const currencySymbolElement = container.querySelector('#currency-symbol') as HTMLElement;

    const sliderLabelMin = container.querySelector('.curr-min') as HTMLElement;
    const sliderLabelMax = container.querySelector('.curr-max') as HTMLElement;
    const errorMessage = container.querySelector('#error-message') as HTMLElement;
    const annualSavingsContainer = container.querySelector('#annual-savings-container') as HTMLElement;
    const liveRateIndicator = container.querySelector('.live-rate-indicator') as HTMLElement;

    // Early return if critical elements are missing
    if (!selectedCurrencyElement || !currencyOptionsList || !amountInput || !sliderInput) {
      console.error('Critical DOM elements not found');
      return;
    }

    //--------------------------------------------------
    // State (inside the effect scope)
    //--------------------------------------------------
    let dropdownOpen = false;
    let exchangeRates: Record<string, any> = {};
    let currencyMin = 0;
    let currencyMax = 0;
    let isInitializing = false;

    const STORAGE_KEY = 'selectedCurrency';
    let currencyInitialized = false;

    // Track if slider or input have been used
    let amountInputUsed = false;
    let sliderUsed = false;
    
    // Flag to prevent tracking during automatic rate refreshes
    let isUserInitiated = false;

    // USD fallback – overwritten by API
    let usdRate = 83.45;
    let currentRate = 83.45;
    let currentSymbol = '$';
    let currentCurrencyCode = 'USD';

    //--------------------------------------------------
    // Cached values to prevent UI flicker
    //--------------------------------------------------
    const valueCache: {
      eximpeAmount: number | null;
      bankAmount: number | null;
      bankPerc: number | null;
      paypalAmount: number | null;
      paypalPerc: number | null;
      payoneerAmount: number | null;
      payoneerPerc: number | null;
      savedAmount: number | null;
    } = {
      eximpeAmount: null,
      bankAmount: null,
      bankPerc: null,
      paypalAmount: null,
      paypalPerc: null,
      payoneerAmount: null,
      payoneerPerc: null,
      savedAmount: null,
    };

    //--------------------------------------------------
    // Helpers
    //--------------------------------------------------
    const fallbackFlags: Record<string, string> = {
      USD: 'https://flagcdn.com/w20/us.png',
      EUR: 'https://flagcdn.com/w20/eu.png',
      GBP: 'https://flagcdn.com/w20/gb.png',
      JPY: 'https://flagcdn.com/w20/jp.png',
      AUD: 'https://flagcdn.com/w20/au.png',
      CAD: 'https://flagcdn.com/w20/ca.png',
      CHF: 'https://flagcdn.com/w20/ch.png',
      CNY: 'https://flagcdn.com/w20/cn.png',
      SGD: 'https://flagcdn.com/w20/sg.png',
      HKD: 'https://flagcdn.com/w20/hk.png',
      NZD: 'https://flagcdn.com/w20/nz.png',
    };

    function debounce<T extends (...args: any[]) => void>(fn: T, delay: number) {
      let t: NodeJS.Timeout | null = null;
      const debounced = (...args: Parameters<T>) => {
        if (t) clearTimeout(t);
        t = setTimeout(() => fn(...args), delay);
      };
      debounced.cancel = () => {
        if (t) clearTimeout(t);
      };
      return debounced;
    }

    function getFlagUrl(code: string, apiUrl: string | null) {
      if (apiUrl && apiUrl.trim()) return apiUrl;
      return fallbackFlags[code] || 'https://flagcdn.com/w20/xx.png';
    }

    function saveSelectedCurrency(code: string) {
      try {
        localStorage.setItem(STORAGE_KEY, code);
      } catch {/* ignore */}
    }

    function getSavedCurrency() {
      try {
        return localStorage.getItem(STORAGE_KEY);
      } catch {
        return null;
      }
    }

    //--------------------------------------------------
    // Loading state helpers
    //--------------------------------------------------
    function showLoadingStates() {
      const skel = '<div class="skeleton"></div>';
      const skelSmall = '<div class="skeleton" style="width:40px;height:16px;"></div>';
      
      // Show loading for all value elements
      if (creditValue) creditValue.innerHTML = skel;
      if (bankCreditAmt) bankCreditAmt.innerHTML = skel;
      if (bankCreditPerc) bankCreditPerc.innerHTML = skelSmall;
      if (paypalCreditAmt) paypalCreditAmt.innerHTML = skel;
      if (paypalCreditPerc) paypalCreditPerc.innerHTML = skelSmall;
      if (payoneerCreditAmt) payoneerCreditAmt.innerHTML = skel;
      if (payoneerCreditPerc) payoneerCreditPerc.innerHTML = skelSmall;
      if (savedAmountEl) savedAmountEl.innerHTML = skel;
      
      // Hide error and show annual savings container
      toggleError(false);
      if (annualSavingsContainer) annualSavingsContainer.style.display = 'flex';
    }

    //--------------------------------------------------
    // Core UI updaters
    //--------------------------------------------------
    function updateFXRateDisplay() {
      if (fxRateInfo) {
        fxRateInfo.textContent = `Live FX Rate: ${currentRate.toFixed(4)} | 0 Markup`;
      }
    }

    function updateSliderLabels() {
      if (sliderLabelMin) sliderLabelMin.textContent = `${currentCurrencyCode} ${sliderInput.min}`;
      if (sliderLabelMax) sliderLabelMax.textContent = `${currentCurrencyCode} ${sliderInput.max}`;
    }

    function toggleError(show: boolean, msg = '') {
      if (errorMessage) {
        errorMessage.style.display = show ? 'block' : 'none';
        errorMessage.textContent = msg;
      }
    }

    function resetValueCache() {
      (Object.keys(valueCache) as (keyof typeof valueCache)[]).forEach((k) => {
        valueCache[k] = null;
      });
    }

    function addFade(elms: HTMLElement[]) {
      elms.forEach((el) => el?.classList.add('fade-transition'));
    }
    function removeFade(elms: HTMLElement[]) {
      elms.forEach((el) => el?.classList.remove('fade-transition'));
    }

    //--------------------------------------------------
    // Currency selection & dropdown - FIXED VERSION
    //--------------------------------------------------
    function toggleDropdown(e: MouseEvent) {
      e.stopPropagation();
      e.preventDefault();
      
      // Prevent multiple rapid clicks during initialization
      if (isInitializing) return;
      
      dropdownOpen = !dropdownOpen;
      
      if (currencyOptionsList) {
        currencyOptionsList.style.display = dropdownOpen ? 'block' : 'none';
      }
      
      if (dropdownOpen) {
        // Use a small delay to ensure the dropdown is rendered before adding the listener
        setTimeout(() => {
          document.addEventListener('click', closeDropdownOutside, true);
        }, 10);
      } else {
        document.removeEventListener('click', closeDropdownOutside, true);
      }
    }

    function closeDropdownOutside(evt: MouseEvent) {
      const target = evt.target as Node;
      
      // More robust containment check
      const isInsideDropdown = selectedCurrencyElement?.contains(target) || 
                              currencyOptionsList?.contains(target);
      
      if (!isInsideDropdown) {
        dropdownOpen = false;
        if (currencyOptionsList) {
          currencyOptionsList.style.display = 'none';
        }
        document.removeEventListener('click', closeDropdownOutside, true);
      }
    }

    // Store reference to the event handler for proper cleanup
    let dropdownToggleHandler: ((e: MouseEvent) => void) | null = null;

    function initializeDropdownHandler() {
      // Remove existing handler if it exists
      if (dropdownToggleHandler && selectedCurrencyElement) {
        selectedCurrencyElement.removeEventListener('click', dropdownToggleHandler);
      }
      
      // Create new handler
      dropdownToggleHandler = toggleDropdown;
      
      // Add new handler
      if (selectedCurrencyElement) {
        selectedCurrencyElement.addEventListener('click', dropdownToggleHandler);
      }
    }

    //--------------------------------------------------
    // API interactions - IMPROVED VERSION
    //--------------------------------------------------
    async function fetchCurrencyRates(isInitialLoad = false) {
      try {
        isInitializing = true;
        
        if (fxRateInfo) {
          fxRateInfo.innerHTML = '<span class="animate-pulse">Loading rates...</span>';
        }
        
        const res = await fetch(
          'https://api.eximpe.com/internal_portal/currency_rate/?direction=INWARD&page_size=10&pacb_enabled=true',
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            }
          }
        );
        
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        
        const data = await res.json();

        if (!data?.results) {
          if (fxRateInfo) fxRateInfo.textContent = 'Failed to load rates.';
          return;
        }

        exchangeRates = {};
        
        // Clear existing options only if we're rebuilding
        if (!currencyInitialized && currencyOptionsList) {
          currencyOptionsList.innerHTML = '';
        }

        data.results.forEach((item: any) => {
          if (item?.fx_currency?.iso_code && item.value) {
            const code = item.fx_currency.iso_code;
            const flag = getFlagUrl(code, item.fx_currency.country_flag_url);
            exchangeRates[code] = {
              value: item.value,
              flag,
              symbol: item.fx_currency.symbol || '',
            };
          }
        });

        if (exchangeRates.USD) usdRate = exchangeRates.USD.value;

        // Build dropdown only once – subsequent refreshes reuse it
        if (!currencyInitialized) {
          await buildCurrencyDropdown();
          await initializeCurrency(isInitialLoad);
          currencyInitialized = true;
          
          // Initialize dropdown handler after everything is set up
          initializeDropdownHandler();
        } else {
          // Already initialized – just refresh rate for current currency
          if (exchangeRates[currentCurrencyCode]) {
            setSelectedCurrency(currentCurrencyCode, !isUserInitiated);
          }
        }
      } catch (error) {
        console.error('Error fetching currency rates:', error);
        if (fxRateInfo) fxRateInfo.textContent = 'Error loading rates.';
      } finally {
        isInitializing = false;
      }
    }

    async function buildCurrencyDropdown() {
      if (!currencyOptionsList) return;
      
      const sorted = Object.keys(exchangeRates).sort();
      
      // Create document fragment for better performance
      const fragment = document.createDocumentFragment();
      
      sorted.forEach((code) => {
        const li = document.createElement('li');
        li.style.cssText = 'padding:8px 12px;display:flex;align-items:center;gap:8px;cursor:pointer;';
        li.innerHTML = `<img src="${exchangeRates[code].flag}" alt="${code} Flag" style="width:14px;height:14px;object-fit:cover;border-radius:2px;" /><span>${code}</span>`;
        
        const img = li.querySelector('img')!;
        img.onerror = () => (img.src = getFlagUrl(code, null));
        
        // Use event delegation instead of individual listeners
        li.dataset.currencyCode = code;
        
        li.addEventListener('mouseover', () => (li.style.background = '#f0f0f0'));
        li.addEventListener('mouseout', () => (li.style.background = '#fff'));
        
        fragment.appendChild(li);
      });
      
      currencyOptionsList.appendChild(fragment);
      
      // Add single delegated click handler
      currencyOptionsList.addEventListener('click', handleCurrencySelection);
    }

    function handleCurrencySelection(event: Event) {
      const target = event.target as HTMLElement;
      const li = target.closest('li');
      
      if (li && li.dataset.currencyCode) {
        event.stopPropagation();
        const code = li.dataset.currencyCode;
        
        setSelectedCurrency(code);
        saveSelectedCurrency(code);
        
        // Close dropdown
        if (currencyOptionsList) currencyOptionsList.style.display = 'none';
        dropdownOpen = false;
        document.removeEventListener('click', closeDropdownOutside, true);
      }
    }

    async function initializeCurrency(isInitialLoad: boolean) {
      // First-time currency init with better error handling
      const saved = getSavedCurrency();
      let initial = 'USD'; // Default fallback
      
      if (saved && exchangeRates[saved]) {
        initial = saved;
      } else if (exchangeRates.USD) {
        initial = 'USD';
      } else if (Object.keys(exchangeRates).length > 0) {
        initial = Object.keys(exchangeRates)[0];
      }
      
      setSelectedCurrency(initial, isInitialLoad);
      saveSelectedCurrency(initial);
    }

    function setSelectedCurrency(code: string, skipTracking = false) {
      const data = exchangeRates[code];
      if (!data) {
        console.warn(`Currency ${code} not found in exchange rates`);
        return;
      }

      if (fxRateInfo) {
        fxRateInfo.innerHTML = '<span class="animate-pulse">Updating rate...</span>';
      }
      
      if (liveRateIndicator) {
        liveRateIndicator.classList.add('animate-pulse');
      }

      currentRate = data.value;
      currentSymbol = data.symbol || '';
      currentCurrencyCode = code;

      // Update UI elements with null checks
      if (selectedCurrencyCodeElement) selectedCurrencyCodeElement.textContent = code;
      if (currencySymbolElement) currencySymbolElement.textContent = currentSymbol;
      
      if (selectedCurrencyFlagElement) {
        selectedCurrencyFlagElement.src = data.flag || getFlagUrl(code, null);
        selectedCurrencyFlagElement.onerror = () => {
          selectedCurrencyFlagElement.src = getFlagUrl(code, null);
        };
      }

      // Recompute min/max (25 USD and 2.5 M INR rules)
      const minInINR = 100 * usdRate;
      currencyMin = Math.floor((minInINR / currentRate) / 5) * 5;
      currencyMax = Math.ceil((2500000 / currentRate) / 1000) * 1000;
      
      if (sliderInput) {
        sliderInput.min = currencyMin.toString();
        sliderInput.max = currencyMax.toString();
      }

      const currentInputVal = parseFloat(amountInput?.value || '0');
      if (isNaN(currentInputVal) || currentInputVal < currencyMin || currentInputVal > currencyMax) {
        if (amountInput) amountInput.value = currencyMin.toString();
        if (sliderInput) sliderInput.value = currencyMin.toString();
      } else {
        if (sliderInput) sliderInput.value = currentInputVal.toString();
      }

      resetValueCache();
      updateFXRateDisplay();
      updateSliderLabels();
      
      // Reset flag - not using it for tracking currency changes anymore
      isUserInitiated = false;
      
      updateCreditValues(amountInput?.value || currencyMin.toString(), currentRate, skipTracking);

      setTimeout(() => {
        if (liveRateIndicator) liveRateIndicator.classList.remove('animate-pulse');
      }, 300);
    }

    //--------------------------------------------------
    // Credit-value updater (with better error handling)
    //--------------------------------------------------
    async function updateCreditValues(amount: string, fx_rate: number, _skipTracking = false) {
      const val = parseFloat(amount);
      if (!amount || isNaN(val)) {
        showNoData('Please enter a valid amount.');
        return;
      }
      if (val < currencyMin) {
        showNoData(`Please enter an amount greater than ${currentCurrencyCode} ${currencyMin}`);
        return;
      }
      if (val > currencyMax) {
        showNoData(`Please enter an amount lesser than ${currentCurrencyCode} ${currencyMax}`);
        return;
      }
      toggleError(false);

      // Always show skeleton/loading state during API call
      const skel = '<div class="skeleton"></div>';
      const skelSmall = '<div class="skeleton" style="width:30px;"></div>';
      
      if (creditValue) creditValue.innerHTML = skel;
      if (bankCreditAmt) bankCreditAmt.innerHTML = skel;
      if (bankCreditPerc) bankCreditPerc.innerHTML = skelSmall;
      if (paypalCreditAmt) paypalCreditAmt.innerHTML = skel;
      if (paypalCreditPerc) paypalCreditPerc.innerHTML = skelSmall;
      if (payoneerCreditAmt) payoneerCreditAmt.innerHTML = skel;
      if (payoneerCreditPerc) payoneerCreditPerc.innerHTML = skelSmall;
      if (savedAmountEl) savedAmountEl.innerHTML = skel;

      if (annualSavingsContainer) annualSavingsContainer.style.display = 'flex';

      const platform_fee_percent = 0.75;
      const collection_charge = 0.5;
      const number_of_transaction_annually = 1;
      const url =
        `https://api.eximpe.com/internal_portal/currency_rate/website_fx_calculator/?platform_fee_percent=${platform_fee_percent}&fx_rate=${fx_rate}&amount=${amount}&collection_charge=${collection_charge}&number_of_transaction_annually=${number_of_transaction_annually}`;
      
      try {
        const res = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        
        const data = await res.json();
        
        if (!data?.eximpe_breakdown?.receiving_amount) {
          showNoData('No data returned from API.');
          return;
        }

        //--------------------------------------------------
        // EximPe breakdown
        //--------------------------------------------------
        const eximpeAmount = data.eximpe_breakdown.receiving_amount;
        valueCache.eximpeAmount = eximpeAmount;
        if (creditValue) creditValue.textContent = formatCurrency(eximpeAmount);

        //--------------------------------------------------
        // Annual savings
        //--------------------------------------------------
        if (typeof data.max_savings === 'number') {
          const annual = data.max_savings * 12;
          valueCache.savedAmount = annual;
          if (savedAmountEl) savedAmountEl.textContent = formatCurrency(annual);
          updateSavedAmount(annual.toString());
        } else {
          if (annualSavingsContainer) annualSavingsContainer.style.display = 'none';
          updateSavedAmount('');
          valueCache.savedAmount = null;
        }

        //--------------------------------------------------
        // Other methods
        //--------------------------------------------------
        const comparisons = [
          { summary: data.bank_summary, amtEl: bankCreditAmt, percEl: bankCreditPerc, key: 'bank' },
          { summary: data.paypal_summary, amtEl: paypalCreditAmt, percEl: paypalCreditPerc, key: 'paypal' },
          { summary: data.payoneer_summary, amtEl: payoneerCreditAmt, percEl: payoneerCreditPerc, key: 'payoneer' },
        ] as const;

        comparisons.forEach(({ summary, amtEl, percEl, key }) => {
          if (summary?.receiving_amount && amtEl && percEl) {
            const amt = summary.receiving_amount;
            const perc = summary.percentage_change;
            valueCache[`${key}Amount` as const] = amt as any;
            valueCache[`${key}Perc` as const] = perc as any;
            amtEl.textContent = formatCurrency(amt);
            percEl.textContent = `↓${perc.toFixed(2)}%`;
          } else if (amtEl && percEl) {
            amtEl.textContent = 'No Data';
            percEl.textContent = '';
            valueCache[`${key}Amount` as const] = null as any;
            valueCache[`${key}Perc` as const] = null as any;
          }
        });
      } catch (error) {
        console.error('Error fetching calculator data:', error);
        showNoData('Error fetching data from server.');
      }
    }

    //--------------------------------------------------
    // Validation helper reused above
    //--------------------------------------------------
    function showNoData(message: string) {
      toggleError(true, message);
      if (annualSavingsContainer) annualSavingsContainer.style.display = 'none';
      
      const elements = [
        creditValue,
        bankCreditAmt,
        bankCreditPerc,
        paypalCreditAmt,
        paypalCreditPerc,
        payoneerCreditAmt,
        payoneerCreditPerc,
      ].filter(Boolean);
      
      addFade(elements);
      
      if (creditValue) creditValue.textContent = 'No Data';
      if (bankCreditAmt) bankCreditAmt.textContent = 'No Data';
      if (paypalCreditAmt) paypalCreditAmt.textContent = 'No Data';
      if (payoneerCreditAmt) payoneerCreditAmt.textContent = 'No Data';
      if (bankCreditPerc) bankCreditPerc.textContent = '';
      if (paypalCreditPerc) paypalCreditPerc.textContent = '';
      if (payoneerCreditPerc) payoneerCreditPerc.textContent = '';
      if (savedAmountEl) savedAmountEl.textContent = '';
      
      updateSavedAmount('');
      resetValueCache();
      
      setTimeout(() => removeFade(elements), 300);
    }

    //--------------------------------------------------
    // Input sync & debounced updater with analytics tracking
    //--------------------------------------------------
    const debouncedUpdate = debounce(() => {
      isUserInitiated = true; // Mark as user-initiated when input changes
      updateCreditValues(amountInput?.value || '0', currentRate, false);
    }, 500);
    
    function syncInputs(e: Event) {
      let val: number;
      
      // Show loading states immediately when user changes input
      showLoadingStates();
      
      if (e.target === amountInput) {
        val = parseFloat(amountInput.value);
        if (!Number.isNaN(val) && sliderInput) sliderInput.value = val.toString();
        
        // Track input value added if this is first time
        if (!amountInputUsed) {
          trackCalculatorEvent(CalculatorUsedEvent.CurrencyCalculatorValueAdded, {
            page: 'Global Collection Page',
            currency: currentCurrencyCode,
            value: val,
          });
          amountInputUsed = true;
        }
      } else if (e.target === sliderInput) {
        val = parseFloat(sliderInput.value);
        if (amountInput) amountInput.value = Number.isNaN(val) ? '' : val.toString();
        
        // Track slider usage if this is first time
        if (!sliderUsed) {
          trackCalculatorEvent(CalculatorUsedEvent.CurrencyCalculatorSliderUsed, {
            page: 'Global Collection Page',
            currency: currentCurrencyCode,
            value: val
          });
          sliderUsed = true;
        }
      }
      debouncedUpdate();
    }
    
    // Add event listeners with proper error handling
    if (amountInput) amountInput.addEventListener('input', syncInputs);
    if (sliderInput) sliderInput.addEventListener('input', syncInputs);

    //--------------------------------------------------
    // Initialisation + 10‑second live refresh
    //--------------------------------------------------
    if (selectedCurrencyFlagElement) {
      selectedCurrencyFlagElement.onerror = () => {
        selectedCurrencyFlagElement.src = 'https://flagcdn.com/w20/xx.png';
      };
    }
    
    // Initialize everything
    fetchCurrencyRates(true); // Mark initial load

    const RATE_REFRESH_MS = 10_000; // 10 seconds
    const rateRefreshInterval = setInterval(() => fetchCurrencyRates(false), RATE_REFRESH_MS);

    //--------------------------------------------------
    // Cleanup - IMPROVED VERSION
    //--------------------------------------------------
    return () => {
      // Clear intervals
      clearInterval(rateRefreshInterval);
      
      // Remove event listeners with proper null checks
      if (amountInput) amountInput.removeEventListener('input', syncInputs);
      if (sliderInput) sliderInput.removeEventListener('input', syncInputs);
      if (dropdownToggleHandler && selectedCurrencyElement) {
        selectedCurrencyElement.removeEventListener('click', dropdownToggleHandler);
      }
      if (currencyOptionsList) {
        currencyOptionsList.removeEventListener('click', handleCurrencySelection);
      }
      
      // Remove document listeners
      document.removeEventListener('click', closeDropdownOutside, true);
      
      // Clear any pending timeouts from debounced function
      debouncedUpdate.cancel?.();
    };
  }, [updateSavedAmount, trackCalculatorEvent]); // Added track to dependencies

  //--------------------------------------------------
  // JSX (unchanged)
  //--------------------------------------------------
  return (
    <div ref={calculatorRef} className="outer-container">
      <div className="transaction-card">
        {/* --- Transaction input section --- */}
        <div>
          <div className="transaction-input-section">
            <div className="transaction-input-group">
              <label htmlFor="amount" className="caption-text">
                Client has paid
              </label>
              <div className="input-currency-container">
                <span id="currency-symbol" className="amount-text"></span>
                <input type="number" id="amount" defaultValue="100" className="amount-text" />
              </div>
            </div>
            {/* Currency dropdown */}
            
        <div className="custom-dropdown">
         <div className="selected-option" id="selected-currency">
         <img id="selected-currency-flag" src="https://flagcdn.com/w20/us.png" alt="Flag" className="flag-img" />
         <div id="selected-currency-code" className="section-heading"></div>
        <ChevronDown className="dropdown-arrow" />
      </div>
     <ul className="options-list" id="currency-options"></ul>
        </div>
          </div>
          <p id="error-message"></p>
        </div>

        {/* Slider */}
        <div>
          <input type="range" className="custom-range" id="slider" defaultValue="0" />
          <div className="slider-range-label caption-text">
            <p className="curr-min"></p>
            <p className="curr-max"></p>
          </div>
        </div>

        {/* Live FX rate */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="fx-rate">
            <div className="live-rate-indicator"></div>
            <p className="fx-rate-info body-text">Loading rates...</p>
          </div>
        </div>

        {/* Credit section */}
        <div className="credit-section">
        <div className="credit-img-24-hrs">
          <p className='text-[#1a9f86]'><Zap aria-label="Credit icon" /></p>
          
           <p className="caption-text">Credit in 24 hours</p>
            </div>

          <div className="credit-display">
            <h3 id="credit-value" className="main-heading fade-transition">
              <div className="skeleton"></div>
            </h3>
            <div className="credit-currency-display">
              <img src={IndiaFlag} className="INR-icon" alt="INR Icon" />
              <p className="section-heading">INR</p>
            </div>
          </div>
        </div>

        {/* Comparison list */}
        <div className="methods-list">
          <div className="method">
            <div className="method-info"><p className="body-text">Banks</p></div>
            <div className="method-value">
              <p id="bank-credit-amt" className="comparison-text fade-transition"></p>
              <p id="bank-credit-perc" className="comparison-text decrease fade-transition"></p>
            </div>
          </div>
          <div className="horizontal-divider"></div>
          <div className="method">
            <div className="method-info"><p className="body-text">PayPal</p></div>
            <div className="method-value">
              <p id="paypal-credit-amt" className="comparison-text fade-transition"></p>
              <p id="paypal-credit-perc" className="comparison-text decrease fade-transition"></p>
            </div>
          </div>
          <div className="horizontal-divider"></div>
          <div className="method">
            <div className="method-info"><p className="body-text">Others</p></div>
            <div className="method-value">
              <p id="payoneer-credit-amt" className="comparison-text fade-transition"></p>
              <p id="payoneer-credit-perc" className="comparison-text decrease fade-transition"></p>
            </div>
          </div>
        </div>

        {/* Annual savings */}
        <div className="annual-savings" id="annual-savings-container">
  <Wallet className="icon" aria-label="Wallet icon" />
  <p className="body-text" style={{ color: '#1a202c' }}>
    Save up to <strong id="saved-amount" className="fade-transition"></strong> annually
  </p>
</div>

      </div>
    </div>
  );
};

export default CurrencyCalculator;